import {VIEW_PRODUCT_EVENT} from '@/constants/events'
import {useLocale, useLocaleEffect} from '@/lib/locale/locale'
import useProductDetails from '@/lib/product/hooks/useProductDetails'
import {hashProduct} from '@/models/productModel'
import {ProductRoute} from '@/routeRegistry'
import type {ProductId} from '@/routes/_shop/product/$productId'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import useLogPageViewEvent from '@/utils/hooks/useLogPageViewEvent'
import useParam from '@/utils/router/useParam'
import NotFound from '@/views/NotFound/NotFound'

import ProductUI from './ProductUI'

export default memo(function Product() {
  useLogPageViewEvent(VIEW_PRODUCT_EVENT)

  const [locale] = useLocale()

  // This will be used as cache for new product request so screen would not be flicker
  const previousProductIdRef = useRef<ProductId>(undefined)
  const initialThreadId = useRef<string>(undefined)

  useLocaleEffect(
    useCallback(function resetWhenLocaleChange() {
      previousProductIdRef.current = undefined
      initialThreadId.current = undefined
    }, []),
  )

  const [productId, setProductId] = useParam(ProductRoute.id, 'productId')

  const [unavailableProductIds, setUnavailableProductIds] = useMutative<Set<string>>(new Set())
  const latestUnavailableProductIds = useLatest(unavailableProductIds)
  const markProductIdsAsUnavailable = useCallback((productIds: ProductId[]) => {
    const hashes = productIds.map(productId => hashProduct(productId))
    const shouldTrigger = hashes.some(hash => !latestUnavailableProductIds.current.has(hash))
    if (!shouldTrigger) return
    setUnavailableProductIds(draft => {
      hashes.forEach(hash => {
        if (draft.has(hash)) return
        draft.add(hash)
      })
      return draft
    })
  }, [])

  const {data, isLoading, isFetching, refetch} = useProductDetails({
    retailer: productId.retailer,
    id: productId.id,
    locale,
  })

  if (isLoading) {
    return <ProductUI isLoading productId={productId} />
  }

  const product = data ? data.products[0] : undefined

  // Only save previousProductId when that product is successfully loaded
  if (product && !product.outOfStock) {
    previousProductIdRef.current = {
      retailer: product.retailer,
      id: product.id,
    }
  }
  // Rollback to the previous product
  // Only if the product is not the same as the previous product (this may happen when user change the country/locale)
  else if (
    previousProductIdRef.current &&
    previousProductIdRef.current.id !== productId.id &&
    previousProductIdRef.current.retailer !== productId.retailer
  ) {
    markProductIdsAsUnavailable([productId])

    void setProductId({
      retailer: previousProductIdRef.current.retailer,

      id: previousProductIdRef.current.id,
    })
    toastErrorMessage('Option is unavailable, please try again later.')
    return null
  } else if (product?.outOfStock) {
    // Do nothing
  }
  // If this is initial load, show not found page
  else {
    return <NotFound />
  }

  if (data && initialThreadId.current === undefined) {
    initialThreadId.current = data.threadId
  }

  return (
    <ProductUI
      isLoading={false}
      isFetching={isFetching}
      productId={productId}
      product={product}
      setProductId={setProductId}
      unavailableProductIds={unavailableProductIds}
      markProductIdsAsUnavailable={markProductIdsAsUnavailable}
      threadId={initialThreadId.current}
      variationThreadId={data ? data.threadId : undefined}
      suggestedPrompts={data ? data.suggestedPrompts : []}
      refetchProduct={refetch}
    />
  )
})
